import React, { useState } from 'react';

/**
 * ArtistPicker -- searchable, filterable artist selector.
 *
 * Props:
 *   artists     [{ id, display_name, username }]  -- full artist list
 *   selectedIds [string]                          -- currently selected artist IDs (as strings)
 *   onToggle    (artistId: number) => void        -- called to select/deselect
 */
const ArtistPicker = ({ artists = [], selectedIds = [], onToggle }) => {
  const [query, setQuery] = useState('');

  const normalize = (str) => (str || '').toLowerCase();

  // Derive display label for each artist
  const label = (artist) => artist.display_name || artist.username;

  // Artists that match the current search query
  const matchesQuery = (artist) =>
    normalize(label(artist)).includes(normalize(query));

  const filteredArtists = artists.filter(matchesQuery);

  // Artists currently selected (resolved to full objects for chip display)
  const selectedArtists = artists.filter((a) =>
    selectedIds.includes(a.id.toString())
  );

  return (
    <div>
      {/* Search input */}
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search artists..."
        className="input-field mb-2 w-full text-gray-400"
        data-testid="artist-search-input"
      />

      {/* Selected artists chips -- always visible above the list */}
      {selectedArtists.length > 0 && (
        <div
          className="flex flex-wrap gap-1 mb-2"
          data-testid="selected-artists"
        >
          <span className="text-gray-400 text-xs self-center mr-1">
            Selected ({selectedArtists.length}):
          </span>
          {selectedArtists.map((artist) => (
            <button
              key={artist.id}
              type="button"
              onClick={() => onToggle(artist.id)}
              className="flex items-center bg-army text-white text-xs px-2 py-0.5 rounded-full hover:bg-army-light"
              data-testid={`remove-artist-${artist.id}`}
              title={`Remove ${label(artist)}`}
            >
              {label(artist)} &times;
            </button>
          ))}
        </div>
      )}

      {/* Filtered artist list */}
      <div className="bg-battle-dark rounded-lg max-h-48 overflow-y-auto">
        {filteredArtists.length === 0 ? (
          <p
            className="text-gray-400 text-sm p-3 text-center"
            data-testid="artist-search-empty"
          >
            No artists match &quot;{query}&quot;
          </p>
        ) : (
          filteredArtists.map((artist) => (
            <label
              key={artist.id}
              className="flex items-center space-x-2 mb-2 cursor-pointer hover:bg-battle-gray p-2 rounded"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(artist.id.toString())}
                onChange={() => onToggle(artist.id)}
                className="form-checkbox text-brand-yellow"
                data-testid={`artist-checkbox-${artist.id}`}
              />
              <span className="text-white">{label(artist)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export default ArtistPicker;
